import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Clock, Mail, MessageSquare, Smartphone, Cloud, HardDrive, Database,
  Key, Save, Download, Plus, Trash2, X, AlertTriangle, Check, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;

// Worktime Settings Component
export const WorktimeSettingsTab = ({ settings, onSave }) => {
  const [formData, setFormData] = useState(settings || {});

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addHoliday = () => {
    const date = prompt('Feiertag hinzufügen (Format: YYYY-MM-DD):');
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setFormData(prev => ({
        ...prev,
        holidays: [...(prev.holidays || []), date]
      }));
    }
  };

  const removeHoliday = (index) => {
    setFormData(prev => ({
      ...prev,
      holidays: prev.holidays.filter((_, i) => i !== index)
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Arbeitszeiten & Urlaubsregelungen
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Working Hours */}
          <div>
            <h3 className="text-lg font-medium mb-4">Arbeitszeiten pro Wochentag</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                const dayNames = {
                  monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch',
                  thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag'
                };
                
                return (
                  <div key={day} className="flex items-center gap-2">
                    <span className="w-20 text-sm font-medium">{dayNames[day]}:</span>
                    <Input
                      type="time"
                      value={formData[`${day}_start`] || ''}
                      onChange={(e) => handleChange(`${day}_start`, e.target.value)}
                      className="w-24"
                    />
                    <span className="text-slate-500">bis</span>
                    <Input
                      type="time"
                      value={formData[`${day}_end`] || ''}
                      onChange={(e) => handleChange(`${day}_end`, e.target.value)}
                      className="w-24"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Break and Vacation Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Mittagspause (Minuten)</label>
              <Input
                type="number"
                value={formData.lunch_break_duration || 60}
                onChange={(e) => handleChange('lunch_break_duration', parseInt(e.target.value))}
                min="0"
                max="120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Urlaubstage pro Jahr</label>
              <Input
                type="number"
                value={formData.annual_vacation_days || 30}
                onChange={(e) => handleChange('annual_vacation_days', parseInt(e.target.value))}
                min="20"
                max="40"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Krankheitstage Limit</label>
              <Input
                type="number"
                value={formData.sick_days_limit || 42}
                onChange={(e) => handleChange('sick_days_limit', parseInt(e.target.value))}
                min="0"
              />
            </div>
          </div>

          {/* Holidays */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Feiertage</h3>
              <Button type="button" onClick={addHoliday} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Feiertag hinzufügen
              </Button>
            </div>
            
            {formData.holidays && formData.holidays.length > 0 ? (
              <div className="space-y-2">
                {formData.holidays.map((holiday, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded">
                    <span>{new Date(holiday).toLocaleDateString('de-DE')}</span>
                    <Button
                      type="button"
                      onClick={() => removeHoliday(index)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">Keine Feiertage definiert</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Arbeitszeiten speichern
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Notification Settings Component
export const NotificationSettingsTab = ({ settings, onSave }) => {
  const [formData, setFormData] = useState(settings || {});

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSwitchChange = (field, checked) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const testEmail = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${BACKEND_URL}/api/settings/test-email`, {
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
        smtp_username: formData.smtp_username,
        smtp_password: formData.smtp_password,
        sender_email: formData.sender_email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Test-E-Mail erfolgreich versendet!');
    } catch (error) {
      alert('E-Mail-Test fehlgeschlagen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Browser Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Browser-Benachrichtigungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Browser-Notifications aktivieren</p>
                <p className="text-sm text-slate-500">Desktop-Benachrichtigungen für wichtige Ereignisse</p>
              </div>
              <Switch
                checked={formData.browser_notifications || false}
                onCheckedChange={(checked) => handleSwitchChange('browser_notifications', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            E-Mail-Benachrichtigungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* SMTP Configuration */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">SMTP-Konfiguration</h3>
                <Switch
                  checked={formData.smtp_enabled || false}
                  onCheckedChange={(checked) => handleSwitchChange('smtp_enabled', checked)}
                />
              </div>

              {formData.smtp_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">SMTP Server</label>
                    <Input
                      value={formData.smtp_host || ''}
                      onChange={(e) => handleChange('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Port</label>
                    <Input
                      type="number"
                      value={formData.smtp_port || 587}
                      onChange={(e) => handleChange('smtp_port', parseInt(e.target.value))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Benutzername</label>
                    <Input
                      value={formData.smtp_username || ''}
                      onChange={(e) => handleChange('smtp_username', e.target.value)}
                      placeholder="your-email@gmail.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Passwort</label>
                    <Input
                      type="password"
                      value={formData.smtp_password || ''}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      placeholder="App-Passwort"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Absender E-Mail</label>
                    <Input
                      type="email"
                      value={formData.sender_email || ''}
                      onChange={(e) => handleChange('sender_email', e.target.value)}
                      placeholder="noreply@skalartech.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Absender Name</label>
                    <Input
                      value={formData.sender_name || 'SKALARTECH System'}
                      onChange={(e) => handleChange('sender_name', e.target.value)}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">TLS verwenden</p>
                        <p className="text-sm text-slate-500">Verschlüsselte Verbindung (empfohlen)</p>
                      </div>
                      <Switch
                        checked={formData.smtp_use_tls || true}
                        onCheckedChange={(checked) => handleSwitchChange('smtp_use_tls', checked)}
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Button type="button" onClick={testEmail} variant="outline" className="w-full">
                      <Mail className="w-4 h-4 mr-2" />
                      Test-E-Mail versenden
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Email Preferences */}
            <div>
              <h3 className="text-lg font-medium mb-4">E-Mail-Benachrichtigungen für:</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Niedrige Lagerbestände</p>
                    <p className="text-sm text-slate-500">Benachrichtigung bei Unterschreitung des Mindestbestands</p>
                  </div>
                  <Switch
                    checked={formData.email_low_stock || true}
                    onCheckedChange={(checked) => handleSwitchChange('email_low_stock', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Urlaubsanträge</p>
                    <p className="text-sm text-slate-500">Neue und genehmigte Urlaubsanträge</p>
                  </div>
                  <Switch
                    checked={formData.email_vacation_requests || true}
                    onCheckedChange={(checked) => handleSwitchChange('email_vacation_requests', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Fahrzeugwartung</p>
                    <p className="text-sm text-slate-500">Fällige Wartungstermine</p>
                  </div>
                  <Switch
                    checked={formData.email_vehicle_maintenance || true}
                    onCheckedChange={(checked) => handleSwitchChange('email_vehicle_maintenance', checked)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Benachrichtigungen speichern
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* WhatsApp/SMS Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            WhatsApp & SMS (Zukünftige Features)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 opacity-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">WhatsApp-Benachrichtigungen</p>
                <p className="text-sm text-slate-500">Integration mit WhatsApp Business API</p>
              </div>
              <Switch disabled />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">SMS-Benachrichtigungen</p>
                <p className="text-sm text-slate-500">SMS-Gateway Integration</p>
              </div>
              <Switch disabled />
            </div>

            <p className="text-sm text-slate-400 italic">
              Diese Features werden in einer zukünftigen Version verfügbar sein.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Backup Settings Component
export const BackupSettingsTab = ({ backupList, onCreateBackup, onReload }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const downloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${BACKEND_URL}/api/settings/backup/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Backup erfolgreich heruntergeladen!');
    } catch (error) {
      console.error('Fehler beim Download:', error);
      alert('Fehler beim Download: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm('Möchten Sie dieses Backup wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${BACKEND_URL}/api/settings/backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Backup erfolgreich gelöscht');
      onReload();
    } catch (error) {
      alert('Fehler beim Löschen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  const restoreBackup = async (filename) => {
    const confirmMessage = `⚠️ WARNUNG: Diese Aktion wird ALLE aktuellen Daten durch das ausgewählte Backup ersetzen!\n\nBackup: ${filename}\n\nMöchten Sie wirklich fortfahren?\n\nHinweis: Es wird empfohlen, vor der Wiederherstellung ein aktuelles Backup zu erstellen.`;
    
    if (!window.confirm(confirmMessage)) return;

    // Double confirmation
    const doubleConfirm = window.confirm('Sind Sie absolut sicher? Diese Aktion kann nicht rückgängig gemacht werden!');
    if (!doubleConfirm) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${BACKEND_URL}/api/settings/backup/restore/${filename}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      let message = `✅ Backup erfolgreich wiederhergestellt!\n\n`;
      message += `Wiederhergestellte Collections: ${result.total_restored}\n`;
      
      if (result.collections_failed && result.collections_failed.length > 0) {
        message += `\n⚠️ Warnung: ${result.collections_failed.length} Collection(s) konnten nicht wiederhergestellt werden.`;
      }
      
      message += `\n\nDie Seite wird jetzt neu geladen...`;
      
      alert(message);
      
      // Reload page to reflect restored data
      window.location.reload();
    } catch (error) {
      alert('❌ Fehler beim Wiederherstellen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Manual Backup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Manuelles Backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-slate-600">
              Erstellen Sie ein vollständiges Backup aller Daten des Systems. 
              Das Backup enthält alle Mitarbeiter, Fahrzeuge, Inventar und Einstellungen.
            </p>
            
            <Button onClick={onCreateBackup} className="w-full" size="lg">
              <Download className="w-5 h-5 mr-2" />
              Backup jetzt erstellen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Backup-Verlauf ({backupList.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {backupList.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Noch keine Backups erstellt</p>
              <p className="text-sm text-slate-400 mt-2">Klicken Sie oben auf "Backup jetzt erstellen"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backupList.map((backup) => (
                <div key={backup.filename} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">{backup.filename}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{formatFileSize(backup.size)}</span>
                      <span>{new Date(backup.created_at).toLocaleString('de-DE')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restoreBackup(backup.filename)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                      title="Backup wiederherstellen"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadBackup(backup.filename)}
                      title="Backup herunterladen"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteBackup(backup.filename)}
                      title="Backup löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cloud Sync (Future Feature) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5" />
            Cloud-Synchronisation (Zukünftiges Feature)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 opacity-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Google Drive Sync</p>
                <p className="text-sm text-slate-500">Automatische Synchronisation mit Google Drive</p>
              </div>
              <Switch disabled />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Dropbox Sync</p>
                <p className="text-sm text-slate-500">Automatische Synchronisation mit Dropbox</p>
              </div>
              <Switch disabled />
            </div>

            <p className="text-sm text-slate-400 italic">
              Cloud-Synchronisation wird in einer zukünftigen Version verfügbar sein.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Modal Components
export const AddEmployeeLoginModal = ({ employees, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${BACKEND_URL}/api/settings/employee-logins`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSuccess();
    } catch (error) {
      alert('Fehler beim Erstellen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Mitarbeiter-Login hinzufügen</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Mitarbeiter wählen</label>
            <select
              value={formData.employee_id}
              onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
              className="w-full p-2 border border-slate-300 rounded-md"
              required
            >
              <option value="">Mitarbeiter auswählen</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} ({emp.employee_number})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Benutzername</label>
            <Input
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              placeholder="benutzername"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Passwort</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Sicheres Passwort"
              required
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Erstelle...' : 'Login erstellen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const AddAdminModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${BACKEND_URL}/api/settings/admin-users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSuccess();
    } catch (error) {
      alert('Fehler beim Erstellen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Admin-Benutzer hinzufügen</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Benutzername</label>
            <Input
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              placeholder="admin_benutzername"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">E-Mail</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="admin@skalartech.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Passwort</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Starkes Admin-Passwort"
              required
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Erstelle...' : 'Admin erstellen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const EditAdminModal = ({ admin, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: admin.username || '',
    email: admin.email || '',
    password: '' // Optional - nur wenn geändert werden soll
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const updateData = {
        username: formData.username,
        email: formData.email
      };
      
      // Nur Passwort hinzufügen, wenn es ausgefüllt wurde
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${BACKEND_URL}/api/settings/admin-users/${admin.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Admin-Benutzer erfolgreich aktualisiert');
      onSuccess();
    } catch (error) {
      alert('Fehler beim Aktualisieren: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Admin-Benutzer bearbeiten</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Benutzername</label>
            <Input
              value={formData.username}
              onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              placeholder="admin_benutzername"
              required
              disabled={admin.username === 'admin'}
              title={admin.username === 'admin' ? 'Haupt-Admin-Benutzername kann nicht geändert werden' : ''}
            />
            {admin.username === 'admin' && (
              <p className="text-xs text-slate-500 mt-1">Haupt-Admin-Benutzername kann nicht geändert werden</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">E-Mail</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="admin@skalartech.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Neues Passwort (optional)</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Leer lassen, um Passwort beizubehalten"
            />
            <p className="text-xs text-slate-500 mt-1">Nur ausfüllen, wenn das Passwort geändert werden soll</p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Speichere...' : 'Änderungen speichern'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ChangePasswordModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      alert('Neue Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${BACKEND_URL}/api/settings/change-password`, {
        old_password: formData.old_password,
        new_password: formData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Passwort erfolgreich geändert!');
      onClose();
    } catch (error) {
      alert('Fehler beim Ändern: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Passwort ändern</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Altes Passwort</label>
            <Input
              type="password"
              value={formData.old_password}
              onChange={(e) => setFormData(prev => ({ ...prev, old_password: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Neues Passwort</label>
            <Input
              type="password"
              value={formData.new_password}
              onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Neues Passwort bestätigen</label>
            <Input
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
              required
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Ändere...' : 'Passwort ändern'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};